// app/routes/api/get-customer-invoices.ts
import { query } from "@solidjs/router";

import { getUserByEmail, updateDB } from "~/_server";
import { getXeroClient } from "~/_server/xero";
import {
	Invoice,
	Invoice as InvoiceType,
	Contact as ContactType,
	Payment,
} from "xero-node";

type InvoiceDTO = {
	invoiceID?: string;
	invoiceNumber?: string;
	date?: string | null;
	dueDate?: string | null;
	status?: Invoice["status"];
	total?: number | null;
	amountDue?: number | null;
	amountPaid?: number | null;
	contact?: { contactID?: string; name?: string; emailAddress?: string };
};

const toDTO = (i: InvoiceType): InvoiceDTO => ({
	invoiceID: i.invoiceID,
	invoiceNumber: i.invoiceNumber,
	date: i.date ? new Date(i.date).toISOString() : null,
	dueDate: i.dueDate ? new Date(i.dueDate).toISOString() : null,
	status: i.status,
	total: i.total ?? null,
	amountDue: i.amountDue ?? null,
	amountPaid: i.amountPaid ?? null,
	contact: i.contact
		? {
				contactID: i.contact.contactID,
				name: i.contact.name,
				emailAddress: i.contact.emailAddress,
			}
		: undefined,
});

const toMs = (d: unknown): number =>
	d ? new Date(d as any).getTime() : -Infinity;

const reverseDate = (d: Date): string =>
	`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

export const createInvoice = async (
	userEmail: string,
	userName: string,
	lineItems: InvoiceType["lineItems"],
	invoiceId: number,
	date: string = "",
): Promise<string | undefined> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(userEmail, true);

		let dateObj;
		if (date == "") {
			dateObj = new Date();
		} else {
			dateObj = new Date(date);
		}

		const invoice = {
			invoiceNumber: `RR-${reverseDate(dateObj)}-${String(invoiceId).padStart(5, "0")}`,
			type: Invoice.TypeEnum.ACCREC,
			contact: { contactID: contact?.contactID ?? null, name: userName },
			lineItems,
			date: dateObj.toISOString(),
			dueDate: dateObj.toISOString(),
			status: Invoice.StatusEnum.AUTHORISED,
		};

		console.log("Creating invoice:", invoice);
		const result = await xeroClient.accountingApi.createInvoices(tenantId, {
			invoices: [invoice],
		});

		return result.body?.invoices?.[0]?.invoiceID ?? undefined;
	} catch (e: unknown) {
		console.error("Error creating invoice:", (e as Error).message);
		return undefined;
	}
};

export const setInvoicePending = async (userId: number, invoiceId: string) => {
	"use server";

	try {
		await updateDB(`USER#${userId}`, `INVOICE#${invoiceId}`, {
			status: "pending",
			_ttl: 60 * 60 * 24 * 30,
		}); // pending for 30 days

		return { success: true };
	} catch (e: unknown) {
		console.error("Error setting invoice pending:", (e as Error).message);

		return { success: false, error: (e as Error).message };
	}
};

export const markInvoiceAsPaid = async (invoiceID: string, amount: number) => {
	try {
		const { xeroClient, tenantId } = await getXeroClient();
		const accountID = "88c6474b-df86-4b3a-934a-dd02e642ae05";

		const payment = {
			invoice: { invoiceID },
			account: { accountID },
			date: new Date().toISOString().split("T")[0],
			amount,
		} as Payment;

		const result = await xeroClient.accountingApi.createPayment(
			tenantId,
			payment,
		);

		return result.body.payments?.[0];
	} catch (error) {
		console.error("Error creating payment:", error);
	}

	return null;
};

const createContact = async (userEmail: string) => {
	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const user = await getUserByEmail(userEmail);

		if (user) {
			const contact = {
				Name: user.name,
				EmailAddress: user.email,
				FirstName: user.firstName,
				LastName: user.lastName,
			} as ContactType;

			const result = await xeroClient.accountingApi.createContacts(tenantId, {
				contacts: [contact],
			});

			return result.body.contacts?.[0];
		}
	} catch (error) {
		console.error("Error creating contact:", error);
	}

	return null;
};

export const getContactByEmail = async (
	email: string,
	createIfNotExists = false,
) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contactRes = await xeroClient.accountingApi.getContacts(
			tenantId,
			undefined,
			`EmailAddress=="${email}"`,
		);

		let contact = contactRes?.body?.contacts?.[0];
		if (!contact?.contactID && createIfNotExists) {
			contact = await createContact(email);

			//throw new Error(`No contact found for ${email}`);
		}

		if (contact?.contactID) return contact;
	} catch (error) {
		console.error("Error fetching contact by email:", error);
	}
};

export const getCustomerInvoices = query(async (email: string) => {
	"use server";
	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(email, false);

		if (contact) {
			// Step 2: Get invoices for this contact
			const invoiceRes = await xeroClient.accountingApi.getInvoices(
				tenantId,
				undefined,
				`Contact.ContactID==Guid("${contact.contactID}")`,
				undefined, // order
				undefined, // IDs
				undefined, // invoiceNumbers
				undefined, // contact IDs
				["AUTHORISED", "PAID"], // statuses
				undefined, // page
				undefined, // includeArchived
				false, // whereOnlyInvoices
			);

			const invoices = (invoiceRes.body.invoices ?? [])
				.slice()
				.filter(inv => inv.status != Invoice.StatusEnum.DELETED)
				.sort((a, b) => toMs(b.date) - toMs(a.date)) // date DESC, nulls last
				.map(toDTO);

			return invoices;
		} else {
			return [];
		}
	} catch (e: unknown) {
		console.error(e);
		return [{ error: (e as Error).message }];
	}
}, "getCustomerInvoices");
